import {
  createInstance,
  ModuleFederation
} from '@module-federation/enhanced/runtime';

import { useMfeUrl } from '../use-mfe-url';

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as axios from 'axios';
import * as EmotionReact from '@emotion/react';
import * as muiMaterial from '@mui/material';

// Define shared dependency structure
interface SharedDependency {
  singleton: boolean;
  lib: () => any;
  version: string;
}

interface SharedDependencies {
  [key: string]: SharedDependency;
}

// Define available modules in the scheduling UI
type SchedulingUiModules =
  | 'KyruusSchedulingUI'
  | 'KyruusManagementUI'
  | 'KyruusCancellationUI'
  | 'KyruusWaitlistUI';

/**
 * Global declaration for webpack injected dependency versions
 */
declare const __DEPENDENCY_VERSIONS__: {
  [key: string]: string;
};

// instantiate the getters outside of the function to keep
// references to the original libs stable. helps with unit testing
const libGetters = {
  react: () => React,
  'react-dom': () => ReactDOM,
  '@emotion/react': () => EmotionReact,
  axios: () => axios,
  '@mui/material': () => muiMaterial
};

/**
 * These are the dependencies shared between this providermatch-consumer application and
 * the imported scheduling MFE.
 * __DEPENDENCY_VERSIONS__ are injected via a custom build plugin
 */
export function getSchedulingUiSharedDependencies(): SharedDependencies {
  return {
    react: {
      singleton: true,
      lib: libGetters['react'],
      version: __DEPENDENCY_VERSIONS__['react']
    },
    '@emotion/react': {
      singleton: true,
      lib: libGetters['@emotion/react'],
      version: __DEPENDENCY_VERSIONS__['@emotion/react']
    },
    'react-dom': {
      singleton: true,
      lib: libGetters['react-dom'],
      version: __DEPENDENCY_VERSIONS__['react-dom']
    },
    axios: {
      singleton: true,
      lib: libGetters['axios'],
      version: __DEPENDENCY_VERSIONS__['axios']
    },
    '@mui/material': {
      singleton: true,
      lib: libGetters['@mui/material'],
      version: __DEPENDENCY_VERSIONS__['@mui/material']
    }
  };
}

/**
 * Load a module federation instance of the scheduling UI micro frontend
 * @param url The URL to the micro frontend manifest
 * @returns A configured module federation instance
 */
export function loadSchedulingUiMfe(url: string): ModuleFederation {
  if (!url) {
    throw new Error('scheduling ui url is missing');
  }
  const schedulingMf = createInstance({
    name: 'scheduling',
    shareStrategy: 'loaded-first',
    remotes: [
      {
        name: 'scheduling',
        entry: url, // 'https://lcl.kyruus.com:4000/current/mf-manifest.json',
        alias: 'scheduling'
      }
    ]
  });

  schedulingMf.registerShared(getSchedulingUiSharedDependencies());

  return schedulingMf;
}

/**
 * Interface for the hook parameters
 */
export interface SchedulingModuleOptions {
  /** URL to the micro frontend manifest */
  url: string;
  /** Name of the module to load from the scheduling UI */
  moduleName: SchedulingUiModules;
}

/**
 * Hook for importing a scheduling module from the scheduling MFE. The modules are exported from
 * the scheduling-ui project
 * @param options The options for loading the module
 * @returns A lazy-loaded React component
 */
export function useSchedulingModule({
  moduleName
}: SchedulingModuleOptions): React.LazyExoticComponent<any> | null {
  const mfeUrl = useMfeUrl('scheduling-ui');

  return React.useMemo(() => {
    if (!mfeUrl) {
      return null;
    }
    const mf = loadSchedulingUiMfe(mfeUrl);
    return React.lazy((): Promise<any> => {
      let myComponent;
      switch (moduleName) {
        case 'KyruusCancellationUI':
          myComponent = mf.loadRemote('scheduling/KyruusCancellationUI');
          break;
        case 'KyruusManagementUI':
          myComponent = mf.loadRemote('scheduling/KyruusManagementUI');
          break;
        case 'KyruusSchedulingUI':
          myComponent = mf.loadRemote('scheduling/KyruusSchedulingUI');
          break;
        case 'KyruusWaitlistUI':
          myComponent = mf.loadRemote('scheduling/KyruusWaitlistUI');
          break;
      }
      return myComponent;
    });
  }, [mfeUrl, moduleName]);
}
